import { Alert, Anchor, Badge, Button, Container, Group, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconPrinter, IconSend } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { PresenceAvatars } from '@/components/presence/PresenceAvatars';
import { useResourcePresence } from '@/hooks/useResourcePresence';
import { presenceRowKey } from '@/lib/presence/presenceFhir';
import {
  buildCms1500FormState,
  CMS1500_DIAGNOSIS_SLOTS,
  CMS1500_SERVICE_LINE_SLOTS,
  type Cms1500FormState,
  type Cms1500ServiceLineForm,
} from './cms1500FormState';
import { formatDateForCms1500 } from './cms1500Utils';
import classes from './Cms1500ClaimPage.module.css';
import { MOCK_CLAIMS, type ClaimRow } from './mockClaims';

const STATUS_COLORS: Record<ClaimRow['status'], string> = {
  active: 'green',
  draft: 'gray',
  cancelled: 'red',
  'entered-in-error': 'orange',
};

const DIAGNOSIS_KEYS = 'ABCDEFGHIJKL'.split('');

function titleCase(value: string): string {
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function Field({
  value,
  onChange,
  readOnly = false,
  className = classes.input,
}: {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <input
      className={className}
      value={value}
      readOnly={readOnly || !onChange}
      onChange={onChange ? (event) => onChange(event.currentTarget.value) : undefined}
    />
  );
}

function Opt({ label, checked = false }: { label: string; checked?: boolean }): JSX.Element {
  return (
    <label className={classes.opt}>
      <input type="checkbox" className={classes.cbox} checked={checked} readOnly /> {label}
    </label>
  );
}

export function Cms1500ClaimPage(): JSX.Element {
  const { claimId } = useParams();
  const claim = useMemo(() => MOCK_CLAIMS.find((c) => c.id === claimId), [claimId]);
  const [form, setForm] = useState<Cms1500FormState | null>(() =>
    claim ? buildCms1500FormState(claim) : null,
  );
  const { presenceByRow, setActiveRow } = useResourcePresence();

  useEffect(() => {
    if (claim) {
      setForm(buildCms1500FormState(claim));
    }
  }, [claim]);

  useEffect(() => {
    if (!claim) {
      setActiveRow(null);
      return;
    }
    setActiveRow({
      rowId: claim.id,
      scope: 'billing-claims',
      subjectResourceType: 'Claim',
    });
    return () => setActiveRow(null);
  }, [claim, setActiveRow]);

  const setField = <K extends keyof Cms1500FormState>(key: K, value: Cms1500FormState[K]): void => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const setDiagnosis = (index: number, value: string): void => {
    setForm((prev) => {
      if (!prev) return prev;
      const diagnoses = [...prev.diagnoses];
      diagnoses[index] = value;
      return { ...prev, diagnoses };
    });
  };

  const setServiceLine = (index: number, patch: Partial<Cms1500ServiceLineForm>): void => {
    setForm((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        serviceLines: prev.serviceLines.map((line, lineIndex) =>
          lineIndex === index ? { ...line, ...patch } : line,
        ),
      };
    });
  };

  if (!claim || !form) {
    return (
      <Container size="sm" py="xl">
        <Stack align="center" gap="sm" py="xl">
          <Title order={4}>Claim not found</Title>
          <Text size="sm" c="dimmed">
            We couldn&apos;t find a claim with id &ldquo;{claimId}&rdquo;.
          </Text>
          <Anchor component={Link} to="/dashboard/billing/claims">
            Back to Billing
          </Anchor>
        </Stack>
      </Container>
    );
  }

  const lineItemCount = claim.encounters.reduce((count, enc) => count + enc.items.length, 0);
  const overflow = Math.max(0, lineItemCount - CMS1500_SERVICE_LINE_SLOTS);

  return (
    <Container size="lg" py="lg">
      <Stack gap="md">
        <Group justify="space-between" align="center" className={classes.printHidden}>
          <Stack gap="xs">
            <Group gap="sm">
              <Anchor component={Link} to={`/dashboard/billing/claims/${encodeURIComponent(claim.id)}`} c="dimmed">
                <IconArrowLeft size={18} />
              </Anchor>
              <Title order={3} fw={700}>
                Insurance Claim for{' '}
                <Text span c="gold.7" inherit>
                  {claim.patientName}
                </Text>
              </Title>
              <Badge variant="light" size="md" color={STATUS_COLORS[claim.status]}>
                {titleCase(claim.status)}
              </Badge>
            </Group>
            {(presenceByRow.get(presenceRowKey('billing-claims', claim.id)) ?? []).length > 0 && (
              <Group gap="xs" pl={28}>
                <Text size="xs" c="dimmed">
                  Also viewing
                </Text>
                <PresenceAvatars
                  actors={presenceByRow.get(presenceRowKey('billing-claims', claim.id)) ?? []}
                />
              </Group>
            )}
          </Stack>
          <Group gap="sm">
            <Button
              variant="filled"
              leftSection={<IconSend size={16} />}
              onClick={() => {
                notifications.show({
                  color: 'blue',
                  title: 'Mock resubmit',
                  message: 'Claim changes were not saved or submitted to Stedi.',
                });
              }}
            >
              Resubmit
            </Button>
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              onClick={() => window.print()}
            >
              Print
            </Button>
          </Group>
        </Group>

        <Alert variant="light" color="yellow" title="Editable prototype — mock resubmit" className={classes.printHidden}>
          Edit claim fields below, then click Resubmit. Changes are not persisted or sent to Stedi yet.
        </Alert>

        <div className={classes.form}>
          <div className={classes.head}>
            <div>
              <p className={classes.headTitle}>CMS-1500 Health Insurance Claim Form</p>
              <p className={classes.approved}>APPROVED BY NATIONAL UNIFORM CLAIM COMMITTEE (NUCC) 02/12</p>
            </div>
            <Group gap="lg" align="flex-start">
              <div>
                <span className={classes.subLabel}>Clearinghouse Reference</span>
                <Text size="xs" fw={600}>
                  {claim.identifier}
                </Text>
              </div>
              <div>
                <span className={classes.subLabel}>Payer Claim</span>
                <Text size="xs" fw={600}>
                  {claim.payerClaimNumber ?? claim.identifier}
                </Text>
              </div>
            </Group>
          </div>

          <div className={classes.grp}>
            <div className={classes.grpMain}>
              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '58%' }} />
                  <col style={{ width: '42%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>1.</span>
                      <span className={classes.lbl}>Payer</span>
                      <span className={classes.subLabel}>Payer name</span>
                      <Field value={form.payer} onChange={(value) => setField('payer', value)} />
                      <span className={classes.subLabel}>Payer ID</span>
                      <Field value={form.payerId} onChange={(value) => setField('payerId', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>1a.</span>
                      <span className={classes.lbl}>Insured&apos;s I.D. Number (For Program in Item 1)</span>
                      <Field value={form.subscriberId} onChange={(value) => setField('subscriberId', value)} />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '36%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>2.</span>
                      <span className={classes.lbl}>Patient&apos;s Name (Last, First, Middle)</span>
                      <Field value={form.patientName} onChange={(value) => setField('patientName', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>3.</span>
                      <span className={classes.lbl}>Patient&apos;s Birth Date / Sex</span>
                      <Field value="" readOnly />
                      <div className={classes.optRow}>
                        <Opt label="M" />
                        <Opt label="F" />
                      </div>
                    </td>
                    <td>
                      <span className={classes.num}>4.</span>
                      <span className={classes.lbl}>Insured&apos;s Name (Last, First, Middle)</span>
                      <Field value={form.insuredName} onChange={(value) => setField('insuredName', value)} />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '36%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>5.</span>
                      <span className={classes.lbl}>Patient&apos;s Address (No., Street)</span>
                      <Field value={form.patientAddress} onChange={(value) => setField('patientAddress', value)} />
                      <span className={classes.subLabel}>City / State / Zip / Telephone</span>
                      <Field
                        value={form.patientCityStateZip}
                        onChange={(value) => setField('patientCityStateZip', value)}
                      />
                    </td>
                    <td>
                      <span className={classes.num}>6.</span>
                      <span className={classes.lbl}>Patient Relationship to Insured</span>
                      <div className={classes.optRow}>
                        <Opt label="Self" checked />
                        <Opt label="Spouse" />
                        <Opt label="Child" />
                        <Opt label="Other" />
                      </div>
                    </td>
                    <td>
                      <span className={classes.num}>7.</span>
                      <span className={classes.lbl}>Insured&apos;s Address (No., Street)</span>
                      <Field value={form.insuredAddress} onChange={(value) => setField('insuredAddress', value)} />
                      <span className={classes.subLabel}>City / State / Zip / Telephone</span>
                      <Field
                        value={form.insuredCityStateZip}
                        onChange={(value) => setField('insuredCityStateZip', value)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '28%' }} />
                  <col style={{ width: '36%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>9.</span>
                      <span className={classes.lbl}>Other Insured&apos;s Name (Last, First, Middle)</span>
                      <Field value="" readOnly />
                    </td>
                    <td>
                      <span className={classes.num}>10.</span>
                      <span className={classes.lbl}>Is Patient&apos;s Condition Related To:</span>
                      <div className={classes.optRow}>
                        <Opt label="Yes" />
                        <Opt label="No" checked />
                      </div>
                    </td>
                    <td>
                      <span className={classes.num}>11.</span>
                      <span className={classes.lbl}>Insured&apos;s Policy Group or FECA Number</span>
                      <Field value="" readOnly />
                      <span className={classes.sub}>c.</span>
                      <span className={classes.lbl}>Insurance Plan Name or Program Name</span>
                      <Field
                        value={form.insurancePlanName}
                        onChange={(value) => setField('insurancePlanName', value)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '64%' }} />
                  <col style={{ width: '36%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>12.</span>
                      <span className={classes.lbl}>Patient&apos;s or Authorized Person&apos;s Signature</span>
                      <Field value="SIGNATURE ON FILE" readOnly />
                    </td>
                    <td>
                      <span className={classes.num}>13.</span>
                      <span className={classes.lbl}>Insured&apos;s or Authorized Person&apos;s Signature</span>
                      <Field value="SIGNATURE ON FILE" readOnly />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div className={classes.grp}>
            <div className={classes.grpMain}>
              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '33%' }} />
                  <col style={{ width: '33%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>14.</span>
                      <span className={classes.lbl}>Date of Current Illness, Injury, or Pregnancy (LMP)</span>
                      <Field value={form.illnessDate} onChange={(value) => setField('illnessDate', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>15.</span>
                      <span className={classes.lbl}>Other Date</span>
                      <Field value={form.otherDate} onChange={(value) => setField('otherDate', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>16.</span>
                      <span className={classes.lbl}>Dates Patient Unable to Work in Current Occupation</span>
                      <Field value={form.unableToWorkDates} onChange={(value) => setField('unableToWorkDates', value)} />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '67%' }} />
                  <col style={{ width: '33%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>17.</span>
                      <span className={classes.lbl}>Name of Referring Provider or Other Source</span>
                      <Field
                        value={form.referringProvider}
                        onChange={(value) => setField('referringProvider', value)}
                      />
                    </td>
                    <td>
                      <span className={classes.num}>18.</span>
                      <span className={classes.lbl}>Hospitalization Dates Related to Current Services</span>
                      <Field
                        value={form.hospitalizationDates}
                        onChange={(value) => setField('hospitalizationDates', value)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '67%' }} />
                  <col style={{ width: '33%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>19.</span>
                      <span className={classes.lbl}>Additional Claim Information (Designated by NUCC)</span>
                      <Field value={form.additionalInfo} onChange={(value) => setField('additionalInfo', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>20.</span>
                      <span className={classes.lbl}>Outside Lab? / $ Charges</span>
                      <div className={classes.optRow}>
                        <Opt label="Yes" />
                        <Opt label="No" checked />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '67%' }} />
                  <col style={{ width: '33%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>21.</span>
                      <span className={classes.lbl}>Diagnosis or Nature of Illness or Injury (ICD-10)</span>
                      <div className={classes.dx}>
                        {DIAGNOSIS_KEYS.slice(0, CMS1500_DIAGNOSIS_SLOTS).map((key, index) => (
                          <div className={classes.dxItem} key={key}>
                            <span className={classes.dxKey}>{key}.</span>
                            <Field
                              value={form.diagnoses[index] ?? ''}
                              onChange={(value) => setDiagnosis(index, value)}
                            />
                          </div>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span className={classes.num}>22.</span>
                      <span className={classes.lbl}>Resubmission Code / Original Ref. No.</span>
                      <span className={classes.subLabel}>Resubmission code (e.g. 7 = replacement)</span>
                      <Field
                        value={form.resubmissionCode}
                        onChange={(value) => setField('resubmissionCode', value)}
                      />
                      <span className={classes.subLabel}>Original payer reference</span>
                      <Field value={form.originalRefNo} onChange={(value) => setField('originalRefNo', value)} />
                      <span className={classes.num}>23.</span>
                      <span className={classes.lbl}>Prior Authorization Number</span>
                      <Field value={form.priorAuth} onChange={(value) => setField('priorAuth', value)} />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={`${classes.tbl} ${classes.svc}`}>
                <colgroup>
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '9%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '13%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '11%' }} />
                  <col style={{ width: '7%' }} />
                  <col style={{ width: '6%' }} />
                  <col style={{ width: '5%' }} />
                  <col style={{ width: '8%' }} />
                </colgroup>
                <thead>
                  <tr>
                    <th className={classes.svcHd}>24A. From</th>
                    <th className={classes.svcHd}>To</th>
                    <th className={classes.svcHd}>B. POS</th>
                    <th className={classes.svcHd}>C. EMG</th>
                    <th className={classes.svcHd}>D. CPT/HCPCS</th>
                    <th className={classes.svcHd}>Modifier</th>
                    <th className={classes.svcHd}>E. Dx Pointer</th>
                    <th className={classes.svcHd}>F. $ Charges</th>
                    <th className={classes.svcHd}>G. Units</th>
                    <th className={classes.svcHd}>H. EPSDT</th>
                    <th className={classes.svcHd}>I. Qual</th>
                    <th className={classes.svcHd}>J. Rendering Provider ID</th>
                  </tr>
                </thead>
                <tbody>
                  {form.serviceLines.map((line, index) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={index}>
                      <td>
                        <span className={classes.svcNo}>{index + 1}</span>
                        <input
                          className={classes.svcInput}
                          value={line.dateFrom}
                          onChange={(event) => setServiceLine(index, { dateFrom: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.dateTo}
                          onChange={(event) => setServiceLine(index, { dateTo: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.pos}
                          onChange={(event) => setServiceLine(index, { pos: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.emg}
                          onChange={(event) => setServiceLine(index, { emg: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.cpt}
                          onChange={(event) => setServiceLine(index, { cpt: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.modifier}
                          onChange={(event) => setServiceLine(index, { modifier: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.dxPointer}
                          onChange={(event) => setServiceLine(index, { dxPointer: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.charges}
                          onChange={(event) => setServiceLine(index, { charges: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.units}
                          onChange={(event) => setServiceLine(index, { units: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.epsdt}
                          onChange={(event) => setServiceLine(index, { epsdt: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.qual}
                          onChange={(event) => setServiceLine(index, { qual: event.currentTarget.value })}
                        />
                      </td>
                      <td>
                        <input
                          className={classes.svcInput}
                          value={line.renderingId}
                          onChange={(event) => setServiceLine(index, { renderingId: event.currentTarget.value })}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '16%' }} />
                  <col style={{ width: '14%' }} />
                  <col style={{ width: '14%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>25.</span>
                      <span className={classes.lbl}>Federal Tax I.D. Number</span>
                      <Field value={form.taxId} onChange={(value) => setField('taxId', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>26.</span>
                      <span className={classes.lbl}>Patient&apos;s Account No.</span>
                      <Field value={form.patientAccountNo} onChange={(value) => setField('patientAccountNo', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>27.</span>
                      <span className={classes.lbl}>Accept Assignment?</span>
                      <div className={classes.optRow}>
                        <Opt label="Yes" checked />
                        <Opt label="No" />
                      </div>
                    </td>
                    <td>
                      <span className={classes.num}>28.</span>
                      <span className={classes.lbl}>Total Charge</span>
                      <Field value={form.totalCharge} onChange={(value) => setField('totalCharge', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>29.</span>
                      <span className={classes.lbl}>Amount Paid</span>
                      <Field value={form.amountPaid} onChange={(value) => setField('amountPaid', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>30.</span>
                      <span className={classes.lbl}>Rsvd for NUCC Use</span>
                      <Field value="" readOnly />
                    </td>
                  </tr>
                </tbody>
              </table>

              <table className={classes.tbl}>
                <colgroup>
                  <col style={{ width: '34%' }} />
                  <col style={{ width: '33%' }} />
                  <col style={{ width: '33%' }} />
                </colgroup>
                <tbody>
                  <tr>
                    <td>
                      <span className={classes.num}>31.</span>
                      <span className={classes.lbl}>Signature of Physician or Supplier Including Degrees or Credentials</span>
                      <Field
                        value={form.renderingProvider}
                        onChange={(value) => setField('renderingProvider', value)}
                      />
                      <span className={classes.subLabel}>Date {formatDateForCms1500(claim.created)}</span>
                    </td>
                    <td>
                      <span className={classes.num}>32.</span>
                      <span className={classes.lbl}>Service Facility Location Information</span>
                      <Field value={form.serviceFacility} onChange={(value) => setField('serviceFacility', value)} />
                    </td>
                    <td>
                      <span className={classes.num}>33.</span>
                      <span className={classes.lbl}>Billing Provider Info &amp; Phone #</span>
                      <Field value={form.billingProvider} onChange={(value) => setField('billingProvider', value)} />
                      <span className={classes.subLabel}>NPI</span>
                      <Field value={form.billingNpi} onChange={(value) => setField('billingNpi', value)} />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {overflow > 0 && (
          <Text size="sm" c="dimmed">
            This claim has {lineItemCount} service lines; only the first {CMS1500_SERVICE_LINE_SLOTS} are shown on the
            CMS-1500 form ({overflow} additional line{overflow === 1 ? '' : 's'} not shown).
          </Text>
        )}
      </Stack>
    </Container>
  );
}
